/**
 * The primitives.
 *
 * There is exactly **one** component that renders a figure, and it takes a
 * `FigureView` rather than a string. That is the point of it: §39.4's
 * provisional mark cannot be remembered on one screen and forgotten on another
 * if no screen is able to render a number without coming through here.
 *
 * `formatMoney` is deliberately not called anywhere in this file, or in any
 * other `.tsx`. Money is formatted once, in the view-model layer where it is
 * tested, and `check-boundaries.mjs` refuses a component that does it a second
 * time — two formatters disagree eventually, and the disagreement surfaces as
 * an affiliate quoting a number their statement does not contain.
 */

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { dayFrom, dayInRange, isoDay, monthGrid, shiftMonth, todayIso } from './calendar.ts';
import { displayLocale } from './i18n.ts';
import { sortRows, type SortDir, type SortValue } from './sorting.ts';
import { shortDay, timeSeriesModel, type ChartSeries, type Grain } from './chart.ts';
import { FOCUS_ATTRIBUTE, FOCUS_CLASS, focusRow } from './focus-row.ts';
import {
  MAP_MAX_SCALE, MAP_MIN_SCALE, countryTotals, mapIntensity, panViewport, viewBoxOf, zoomViewport,
  type MapViewport,
} from './world-map.ts';
import { tableCsv, tableXls, tableXml } from './present.ts';
import { Icon, type IconName } from './icons.tsx';
import { kitText } from './messages.ts';
import { visibleOptions } from './option-filter.ts';
import { splitCurrency, type ConversionsView, type CurrencyView, type CurrencyViewControl, type FigureView } from './present.ts';
import { stripRuleReferences, stripRuleReferencesInFragment } from './strip-rule-references.ts';
import { explainFailure } from './client.ts';

function userText(value: string): string {
  return stripRuleReferences(value);
}

function userTextNode(children: ReactNode): ReactNode {
  if (typeof children === 'string') return userText(children);
  if (Array.isArray(children)) {
    return children.map((child) =>
      typeof child === 'string' ? stripRuleReferencesInFragment(child) : userTextNode(child),
    );
  }
  return children;
}

/**
 * A two-axis time series — the shape the owner asked for on 2026-09-29,
 * because "yamule clear understanding yete".
 *
 * Two series that do not share a unit share an x-axis and nothing else: clicks
 * on the left, signups on the right, each against its own scale, each named in
 * the axis label beside it. Plotting both against one axis is what makes a
 * signup line look flat beside clicks and tells the reader nothing.
 *
 * Every number comes from `timeSeriesModel` in `chart.ts`, which is tested;
 * this turns the result into SVG and computes nothing of its own.
 *
 * The series are told apart by dash pattern as well as by colour (§8 — never
 * colour alone), and each point carries a `<title>` so the figure is readable
 * by pointer and by screen reader without a tooltip layer.
 */
export function TimeSeries({
  days,
  series,
  leftLabel,
  rightLabel,
  emptyNote,
  height = 170,
  grain,
  onGrain,
  grainLabels,
}: {
  readonly days: readonly string[];
  readonly series: readonly ChartSeries[];
  readonly leftLabel: string;
  readonly rightLabel?: string;
  /** Said under the chart when every figure is zero. */
  readonly emptyNote?: string;
  readonly height?: number;
  /**
   * Day, month or year. The caller rolls its own rows up (`rollUp`) and hands
   * the buckets in as `days`; this draws whatever it is given and only offers
   * the control, because the arithmetic of a roll-up belongs to whoever knows
   * whether a series is a count or a rate.
   */
  readonly grain?: Grain;
  readonly onGrain?: (next: Grain) => void;
  /** Translated names for the three, in the caller's own language. */
  readonly grainLabels?: Readonly<Record<Grain, string>>;
}) {
  /*
   * Measured, and drawn at its real size.
   *
   * It used to lay out at a fixed 960 and scale by the viewBox, which made the
   * height a function of the window: 273px on a desktop for a plot that needs
   * 170, and every label scaled with it, so the tick text was a different size
   * from the table beside it at every width. Drawing at the measured width
   * keeps the height fixed and lets the type come from the theme's own scale
   * (`--text-table`), which is what the rest of the console uses.
   *
   * 960 until the observer first reports, so the first paint is a chart and
   * not an empty box.
   */
  const box = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(960);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry?.contentRect.width ?? 0);
      // Only on a real change: writing the same width back would re-render on
      // every observation for nothing.
      if (w > 0) setWidth((prev) => (Math.abs(prev - w) > 1 ? w : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const model = useMemo(
    () => timeSeriesModel({ days, series, width, height }),
    [days, series, width, height],
  );
  if (days.length === 0) return null;
  const { plot } = model;
  const right = model.width - plot.right;
  const bottom = model.height - plot.bottom;
  const hasRight = series.some((s) => s.axis === 'right');

  return (
    <div className="timeseries" ref={box}>
      <svg
        className="timeseries-svg"
        width={model.width}
        height={model.height}
        viewBox={`0 0 ${model.width} ${model.height}`}
        role="img"
        aria-label={`${leftLabel}${rightLabel ? ` and ${rightLabel}` : ''}, by day`}
      >
        {/* Gridlines on the left axis only: a second set on the right scale
            would draw two grids that almost line up, which reads as a
            rendering fault rather than as two scales. */}
        {model.left.ys.map((y, i) => (
          <line
            key={`grid-${model.left.ticks[i]}`}
            x1={plot.left} x2={right} y1={y} y2={y}
            stroke="var(--line-soft)" strokeWidth="1"
          />
        ))}
        <line x1={plot.left} x2={right} y1={bottom} y2={bottom} stroke="var(--line)" strokeWidth="1" />

        {model.left.ticks.map((t, i) => (
          <text
            key={`lt-${t}`} x={plot.left - 8} y={model.left.ys[i]! + 4}
            textAnchor="end" className="timeseries-tick"
          >{t}</text>
        ))}
        {hasRight ? model.right.ticks.map((t, i) => (
          <text
            key={`rt-${t}`} x={right + 8} y={model.right.ys[i]! + 4}
            textAnchor="start" className="timeseries-tick"
          >{t}</text>
        )) : null}

        {model.xLabels.map((l) => (
          <text key={l.day} x={l.x} y={bottom + 20} textAnchor="middle" className="timeseries-tick">{l.text}</text>
        ))}

        {model.series.map((s) => (
          <g key={s.key}>
            <path
              d={s.path} fill="none" stroke={s.colour} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round"
              {...(s.dashed ? { strokeDasharray: '6 4' } : {})}
            />
            {s.points.map((p) => (
              <circle key={`${s.key}-${p.day}`} cx={p.x} cy={p.y} r="3" fill={s.colour}>
                <title>{`${shortDay(p.day)} — ${s.label}: ${p.value}`}</title>
              </circle>
            ))}
          </g>
        ))}

        {/* The axis names, turned up the side rather than floated above: the
            reader needs to know which scale they are reading at the moment
            they look at the number, not at the top of the card. */}
        <text
          className="timeseries-axis-name" textAnchor="middle"
          transform={`translate(12 ${(plot.top + bottom) / 2}) rotate(-90)`}
        >{leftLabel}</text>
        {hasRight && rightLabel ? (
          <text
            className="timeseries-axis-name" textAnchor="middle"
            transform={`translate(${model.width - 10} ${(plot.top + bottom) / 2}) rotate(90)`}
          >{rightLabel}</text>
        ) : null}
      </svg>

      <div className="timeseries-foot">
        {grain && onGrain && grainLabels ? (
          <label className="timeseries-grain">
            <span className="sr-only">{grainLabels[grain]}</span>
            <select
              value={grain}
              onChange={(e) => onGrain(e.target.value as Grain)}
              aria-label={grainLabels[grain]}
            >
              <option value="day">{grainLabels.day}</option>
              <option value="month">{grainLabels.month}</option>
              <option value="year">{grainLabels.year}</option>
            </select>
          </label>
        ) : null}
        <div className="timeseries-legend">
        {model.legend.map((s) => (
          <span key={s.key} className="timeseries-legend-item">
            <svg className="timeseries-legend-swatch" viewBox="0 0 26 10" aria-hidden="true">
              <line
                x1="0" y1="5" x2="26" y2="5" stroke={s.colour} strokeWidth="2"
                {...(s.dashed ? { strokeDasharray: '5 3' } : {})}
              />
              <circle cx="13" cy="5" r="3" fill={s.colour} />
            </svg>
            {s.label}
          </span>
        ))}
        </div>
      </div>
      {model.empty && emptyNote ? <p className="timeseries-empty">{emptyNote}</p> : null}
    </div>
  );
}

/**
 * Bring the row a link was about into view, and mark it.
 *
 * The screen calls this with whatever id its route carried; the rows carry
 * `data-focus-id`. Nothing else is needed on either side, which is the point —
 * the destinations are a dozen different tables and the only thing they can
 * all agree on is an attribute.
 *
 * The arithmetic and the giving-up live in `focus-row.ts`, where they are
 * tested; this is the DOM and the effect.
 */
export function useFocusRow(id: string | undefined): void {
  useEffect(() => {
    if (!id || typeof document === 'undefined') return;
    const handle = focusRow(id, {
      find: (wanted) => {
        const el = document.querySelector(`[${FOCUS_ATTRIBUTE}="${CSS.escape(wanted)}"]`);
        if (!(el instanceof HTMLElement)) return null;
        return {
          scrollIntoView: () => el.scrollIntoView({ block: 'center', behavior: 'smooth' }),
          mark: () => el.classList.add(FOCUS_CLASS),
          unmark: () => el.classList.remove(FOCUS_CLASS),
        };
      },
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (h) => window.clearTimeout(h as number),
    });
    return () => handle.cancel();
  }, [id]);
}

/**
 * A table's rows, sortable, without the parent having to hold the hook.
 *
 * Wiring `useSort` into a screen means placing it above every early return
 * that screen has (React counts hooks per render; tools/check-hooks.mjs). On
 * sixty-nine tables across three consoles that is sixty-nine chances to get
 * the placement wrong and a panel that throws the moment its data arrives —
 * which happened twice on 2026-09-29 before the check existed.
 *
 * This holds the hook itself, so a table becomes sortable by wrapping it and
 * nothing about the screen's control flow changes:
 *
 *   <Sorted rows={view.rows} by={{ clicks: (r) => r.clicks }}>
 *     {(ordered, sort) => <table>…<SortTh sort={sort} col="clicks">…</table>}
 *   </Sorted>
 *
 * `by` gives each sortable column its value. Pass a formatted cell through
 * `sortableCell` and it orders by the number it prints rather than as text.
 */
export function Sorted<Row, V extends Readonly<Record<string, (row: Row) => string | number | null | undefined>>>({
  rows,
  by,
  initial,
  children,
}: {
  rows: readonly Row[];
  by: V;
  initial?: { key: keyof V & string; dir?: SortDir };
  children: (ordered: readonly Row[], sort: SortState<keyof V & string>) => ReactNode;
}) {
  const { rows: ordered, sort } = useSort(rows, by, initial);
  return <>{children(ordered, sort)}</>;
}

/**
 * A sparkline, drawn from the rows the table below it already shows.
 *
 * Never from a second query: two queries answering the same question at two
 * moments is how a chart comes to disagree with the table under it.
 */
export function Spark({ points, tone }: { points: readonly number[]; tone: 'accent' | 'provisional' }) {
  if (points.length < 2) return null;
  /*
   * A series with a hole in it is not drawn.
   *
   * One missing field — a row the API stopped sending, a fixture that never
   * had it — makes one point `undefined`, `Math.max` returns NaN, and *every*
   * y below becomes NaN: the path renders as `d="M0.00,NaN L3.45,NaN …"`,
   * which the browser refuses and nothing else mentions. The card then carries
   * an empty rectangle where its trend was, and no screen says why.
   *
   * Drawing the points we do have would be worse than drawing none: a line
   * through a gap asserts a value that was never reported. So the figure keeps
   * its number and loses its sparkline, which is the honest half.
   */
  if (!points.every((p) => Number.isFinite(p))) return null;

  const w = 100;
  const h = 28;
  const max = Math.max(...points, 1);
  const step = w / (points.length - 1);
  const xy = points.map((p, i) => [i * step, h - (p / max) * (h - 3) - 1.5] as const);
  const line = xy.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;
  const last = xy.at(-1)!;
  /*
   * The trend is context for the figure above it, so it is drawn in the
   * de-emphasis ink and only today's point takes the accent. It used to be
   * amber whenever the figure was provisional — a state colour spent on a
   * chart line, on every card of a dashboard at once. The state now travels
   * in the line itself: a provisional series is dashed, pencilled in like the
   * figure it sits under.
   */
  const stroke = 'var(--ink-faint)';
  const id = 'spark-area';

  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.2" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path
        d={line}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        {...(tone === 'provisional' ? { strokeDasharray: '3 2.5' } : {})}
      />
      {/* The endpoint is where the affiliate's eye goes: it is today. */}
      <circle cx={last[0]} cy={last[1]} r="2.25" fill="var(--accent)" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/**
 * A figure appears at its value. It does not count up to it.
 *
 * A count-up renders roughly forty wrong numbers before the right one, and this
 * is a screen where an affiliate glances at a total and looks away. §39.4 is
 * about a figure never misleading anyone about what it is worth; an animation
 * that is briefly wrong breaks that for the sake of a flourish. The card still
 * arrives with motion — it is the *entrance* that animates, not the number.
 *
 * (Money could not have animated in any case: an amount is a string precisely
 * because §1.3a will not let it become a float, and parsing it back into one to
 * tween it is the exact coercion the rule exists to prevent. Applying the same
 * answer to counts is consistency, not a limitation.)
 */
/*
 * `emphasis="hero"` marks the one figure a dashboard leads with — exactly one
 * per view, chosen by the screen, never by position. A row of equal cards
 * gave every number the same weight, so none of them was the answer.
 *
 * The currency code is set smaller than the digits it belongs to, and inside
 * the same element with its space, so a copied figure still reads
 * "412,000.00 GBP".
 */
/*
 * `attention` is a different thing from provisional, and was being passed as
 * it: "2 consumers not moving" is a warning about the platform, not a figure
 * that might still change. Under the ledger marks, provisional means a dotted
 * rule — "pencilled in" — so a warning drawn that way said the wrong thing.
 * Attention has its own mark: a caution triangle and the value in amber.
 */
export function Figure({
  view,
  spark,
  emphasis,
  attention,
  overdue,
  onOpen,
  openLabel,
}: {
  view: FigureView;
  spark?: readonly number[];
  emphasis?: 'hero';
  attention?: boolean;
  /** A failure or a late payment — the diamond and the value in red. */
  overdue?: boolean;
  /**
   * Opens the working behind the figure (owner request, 2026-09-29: a card
   * whose number nothing explains is a number the reader has to take on
   * trust). The card becomes a real `<button>` rather than a div with a click
   * handler, so it is reachable by keyboard and announced as something that
   * does a thing — a card that opens on click and not on Enter is a card half
   * the readers cannot use.
   */
  onOpen?: () => void;
  /** What opening it gives, for the accessible name. */
  openLabel?: string;
}) {
  const { amount, unit } = splitCurrency(view.value);
  const classes = [
    'figure',
    view.provisional ? 'is-provisional' : '',
    overdue ? 'is-overdue' : attention ? 'is-attention' : '',
    emphasis === 'hero' ? 'is-hero' : '',
    onOpen ? 'is-openable' : '',
  ].filter(Boolean).join(' ');
  const body = (
    <>
      <span className="figure-label">{view.label}</span>
      <span className="figure-value">
        {amount}
        {unit ? <span className="figure-unit">{` ${unit}`}</span> : null}
      </span>
      {spark ? <Spark points={spark} tone={view.provisional ? 'provisional' : 'accent'} /> : null}
      {/* §21.7b — the figure says it was converted, and whether one rate did
          it. The rate, its source, its as_of and its policy are in the
          `ConversionNotes` block on the same screen, keyed by the marker this
          mark carries — one note for every figure converted the same way,
          because forty repetitions of one sentence is a sentence nobody
          reads. */}
      {view.conversion ? (
        <span className={`figure-converted${view.conversion.mixed ? ' is-mixed' : ''}`}>
          {userText(view.conversion.mark)}
        </span>
      ) : null}
      {/* §39.4 — text, not a tooltip. The affiliate who pastes the number into
          a spreadsheet never hovered it. */}
      {view.note ? <span className="figure-note">{userText(view.note)}</span> : null}
    </>
  );
  if (!onOpen) return <article className={classes}>{body}</article>;
  return (
    <button
      type="button"
      className={classes}
      onClick={onOpen}
      aria-label={openLabel ? `${view.label}: ${view.value}. ${openLabel}` : undefined}
    >
      {body}
      {/* The card says it opens. An affordance nobody can see is one nobody
          uses, and "everything is clickable" is not something a reader guesses
          from a dashboard of cards that mostly are not. */}
      <span className="figure-open" aria-hidden="true">{kitText('figure.open')}</span>
    </button>
  );
}

/**
 * §21.7b — the rate, its source, its `as_of` and the rate policy, said once
 * for every figure that shares them.
 *
 * Rendered beside the figures it describes rather than at the top of the page,
 * and never collapsed behind a control: a reader who has to open something to
 * find out what rate produced a number is a reader who will not.
 */
export function ConversionNotes({ view }: { view: ConversionsView | undefined }) {
  if (!view || view.notes.length === 0) return null;
  return (
    <div className="conversion-notes">
      {view.notes.map((note) => (
        <div className="conversion-note" key={note.key}>
          <p className="conversion-summary">
            {note.marker ? <span className="conversion-marker">{note.marker}</span> : null}
            {userText(note.summary)}
          </p>
          {/* §21.7b — what the operator asked for, beside what this figure
              got. They disagree legitimately, and a reader shown only the
              outcome cannot tell a rule from a setting being ignored. */}
          {note.timing ? <p className="conversion-timing">{userText(note.timing)}</p> : null}
          <ul className="conversion-rates">
            {note.rates.map((rate) => (
              <li key={`${rate.from}-${rate.to}-${rate.asOf}-${rate.rate}`}>
                {userText(rate.sentence)}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/**
 * §21.7 — which of the two views this is, and the way to the other one.
 *
 * The state is always written out; the button appears only where there is
 * somewhere to go. That asymmetry is E37's correction to the language picker:
 * a control offering an action it does not have is worse than a sentence.
 */
export function CurrencyViewSwitch({
  control,
  onView,
}: {
  control: CurrencyViewControl | undefined;
  onView: (view: CurrencyView) => void;
}) {
  if (!control) return null;
  return (
    <div className="currency-view">
      <p className="currency-view-showing">{userText(control.showing)}</p>
      {control.action && control.toView ? (
        <button
          type="button"
          className="copy"
          onClick={() => onView(control.toView as CurrencyView)}
        >
          {userText(control.action)}
        </button>
      ) : null}
      {control.inheritedNote ? (
        <p className="currency-view-note">{userText(control.inheritedNote)}</p>
      ) : null}
      {control.scopeNote ? (
        <p className="currency-view-note">{userText(control.scopeNote)}</p>
      ) : null}
    </div>
  );
}

/** Label/value rows for descriptive metadata — not KPIs. No accent bar, no sparkline. */
export function Facts({
  items,
}: {
  items: readonly { readonly label: string; readonly value: ReactNode }[];
}) {
  return (
    <dl className="facts">
      {items.map((item) => (
        <div className="fact" key={item.label}>
          <dt className="fact-label">{item.label}</dt>
          <dd className="fact-value">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Panel({
  title,
  aside,
  children,
  collapsible = false,
  defaultOpen = true,
  count,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  /**
   * A panel the reader can fold away.
   *
   * Off by default, and it should stay off for anything a reader came to the
   * screen to see — §6 of DESIGN.md: a figure behind a control somebody has to
   * open is a figure most readers never see. It earns its place on the long
   * supporting panels that sit under the one that matters, where the choice is
   * between folding them and scrolling past them every time.
   */
  collapsible?: boolean;
  /** Open unless the caller says otherwise: hidden-by-default hides things. */
  defaultOpen?: boolean;
  /** Shown beside the title so a folded panel still says how much is inside. */
  count?: number;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  if (!collapsible) {
    return (
      <section className="panel">
        <header>
          <h2>{userText(title)}</h2>
          {/*
           * Always wrapped, never dropped in loose. A caller passing a
           * fragment of two things — an export pair and a picker — put both
           * straight into the header's own flex row, where they competed with
           * the heading for width and wrapped: on Reports → Breakdown the CSV
           * and XML buttons stacked one above the other while the heading
           * broke across two lines. The wrapper is `flex-shrink: 0`, so the
           * heading gives way instead.
           */}
          {aside === undefined || aside === null ? null : (
            <div className="panel-aside">{aside}</div>
          )}
        </header>
        <div className="body">{children}</div>
      </section>
    );
  }

  return (
    <section className={open ? 'panel is-collapsible' : 'panel is-collapsible is-folded'}>
      <header>
        {/*
         * The whole heading is the control, not a chevron beside it: a target
         * the width of the panel is one nobody has to aim at, and the heading
         * is what a reader is already looking at when they decide to fold it.
         */}
        <h2>
          <button
            type="button"
            className="panel-fold"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name={open ? 'open' : 'close'} size={14} className="panel-fold-mark" />
            {userText(title)}
            {count === undefined ? null : <span className="count count-badge">{count}</span>}
          </button>
        </h2>
        {aside === undefined || aside === null ? null : (
          <div className="panel-aside">{aside}</div>
        )}
      </header>
      {/* `hidden` rather than unmounting: a folded panel keeps its scroll
          position, its sort and anything the reader typed into it. */}
      <div className="body" id={bodyId} hidden={!open}>{children}</div>
    </section>
  );
}

/**
 * Sorting a table by one of its columns.
 *
 * Asked for by the product owner on 2026-09-28, who wanted what the reference
 * console has: every column head a control, with the direction showing.
 *
 * It sorts the **source** rows, never the view's. A view row holds finished
 * text — "2,500.00 NGN", "1,284" — and ordering that as text puts 10 before 9,
 * while parsing it back into a number means guessing the reader's decimal
 * separator, which differs by locale in a kit that formats by locale. The
 * source row still has `deposits_minor` and a count, so the comparison is on
 * the figure and the screen is built from the result.
 *
 * Nulls sort last in both directions: a row with no figure is not the smallest
 * figure, and flipping the direction to hunt for one is not a thing anybody
 * wants to do.
 */
export interface SortState<K extends string> {
  readonly key: K | null;
  readonly dir: SortDir;
  /** The header calls this; a second click on the same column reverses it. */
  readonly by: (key: K) => void;
}

export function useSort<
  Row,
  /*
   * Inferred from `values`, not from `initial`: naming one starting column
   * would otherwise narrow the key type to that single column, and every other
   * `SortTh` on the table would fail to typecheck against it.
   */
  V extends Readonly<Record<string, (row: Row) => string | number | null | undefined>>,
>(
  rows: readonly Row[],
  /** What each sortable column compares on. A column absent here cannot sort. */
  values: V,
  initial?: { key: keyof V & string; dir?: SortDir },
): { rows: readonly Row[]; sort: SortState<keyof V & string> } {
  type K = keyof V & string;
  const [key, setKey] = useState<K | null>(initial?.key ?? null);
  const [dir, setDir] = useState<SortDir>(initial?.dir ?? 'desc');

  const by = (next: K) => {
    if (next === key) { setDir((d) => (d === 'asc' ? 'desc' : 'asc')); return; }
    setKey(next);
    // A new column starts descending: every column worth sorting here is a
    // quantity, and "most first" is what somebody clicking a figure wants.
    setDir('desc');
  };

  const sorted = useMemo(() => {
    if (key === null || !(key in values)) return rows;
    return sortRows(rows, values[key] as SortValue<Row>, dir);
  }, [rows, key, dir, values]);

  return { rows: sorted, sort: { key, dir, by } };
}

/**
 * A column heading that sorts.
 *
 * The whole heading is the control, and the mark is always present rather than
 * appearing on hover: a column that can be sorted should say so before it is
 * touched, and a row of headings where only one has a mark reads as one
 * sortable column rather than as the sorted one.
 */
export function SortTh<K extends string>({
  sort,
  col,
  align,
  children,
}: {
  sort: SortState<K>;
  col: K;
  /** `num` for a figure column, matching the cells under it. */
  align?: 'num' | 'date';
  children: ReactNode;
}) {
  const active = sort.key === col;
  return (
    <th
      className={align}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className="sort-th" onClick={() => sort.by(col)}>
        <span>{children}</span>
        <span className="sort-mark" aria-hidden="true">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </button>
    </th>
  );
}

/**
 * How many rows a paged list is currently asking for.
 *
 * These endpoints page by limit, not by cursor: they take `limit` and answer
 * with `has_more`. So "Show more" raises the ask and refetches, and the hook
 * exists so four panels do not each invent their own step and their own
 * ceiling. 1000 is the API's own MAX_LIMIT — past it the server clamps
 * silently, and a button that keeps offering more while nothing else arrives
 * is a button that lies.
 */
export const PAGE_STEP = 100;
export const PAGE_CEILING = 1000;

export function usePaging(step: number = PAGE_STEP) {
  const [limit, setLimit] = useState(step);
  return {
    limit,
    /** Passed straight into a query: the API wants it as a string. */
    limitParam: String(limit),
    atCeiling: limit >= PAGE_CEILING,
    more: () => setLimit((n) => Math.min(n + step, PAGE_CEILING)),
    /** Back to one page — for when the filters change under it. */
    reset: () => setLimit(step),
  };
}

/**
 * The end of a list that has more behind it.
 *
 * Audit of 2026-09-28: 209 tables across the four consoles and one of them
 * could page. Thirty-one responses already carried `has_more`, and every one of
 * those screens answered it with a sentence — "…and more" — which tells a
 * reader their list is incomplete and offers them nothing to do about it.
 *
 * Cursor, not page numbers, because that is what the endpoints give: they
 * return `has_more` and a cursor and do not count the rest, so "Page 3 of 97"
 * could only be invented. `Showing 50` is true; `1–50 of 4,812` would not be.
 */
export function Pager({
  shown,
  hasMore,
  busy = false,
  atCeiling = false,
  onMore,
}: {
  /** How many rows are on screen now. */
  shown: number;
  hasMore: boolean;
  busy?: boolean;
  /**
   * The API will not return more however often this is pressed. Saying so is
   * the point: a list that stays truncated under a button that keeps offering
   * to un-truncate it is worse than the sentence this replaced.
   */
  atCeiling?: boolean;
  onMore: () => void;
}) {
  if (shown === 0) return null;
  return (
    <div className="pager">
      <span className="pager-count">
        {hasMore ? kitText('ui.pager.shown', { n: String(shown) })
                 : kitText('ui.pager.all', { n: String(shown) })}
      </span>
      {hasMore && !atCeiling ? (
        <button type="button" className="copy" onClick={onMore} disabled={busy}>
          {busy ? kitText('ui.pager.loading') : kitText('ui.pager.more')}
        </button>
      ) : null}
      {hasMore && atCeiling ? (
        <span className="pager-ceiling">{kitText('ui.pager.ceiling')}</span>
      ) : null}
    </div>
  );
}

/**
 * CSV, XML and a spreadsheet for the table beside it.
 *
 * It existed on three of the 209 tables, built inline each time and dropped
 * into a `Panel`'s aside as a bare `page-head-actions` — a page-header class
 * inside a panel header, which is why it sat at the wrong height. One
 * component, one place in the header, everywhere.
 *
 * `keys` are stable English element names for the XML and are never the
 * headers: a column renamed, or read in Spanish, must not rename the field in
 * a file somebody's spreadsheet already points at.
 */
export function TableActions({
  title,
  headers,
  keys,
  rows,
  filename,
  children,
}: {
  title: string;
  headers: readonly string[];
  keys: readonly string[];
  rows: readonly (readonly string[])[];
  filename: string;
  /** Anything else belonging in the header, before the downloads. */
  children?: ReactNode;
}) {
  const save = (body: string, type: string, ext: string) => {
    const url = URL.createObjectURL(new Blob([body], { type }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="table-actions">
      {children}
      {rows.length === 0 ? null : (
        <>
          <span className="table-actions-label">{kitText('ui.export.label')}</span>
          <button
            type="button"
            className="copy btn-sm"
            onClick={() => save(tableCsv(title, headers, rows), 'text/csv', 'csv')}
          >
            {kitText('ui.export.csv')}
          </button>
          <button
            type="button"
            className="copy btn-sm"
            onClick={() => save(tableXml(title, keys, rows), 'application/xml', 'xml')}
          >
            {kitText('ui.export.xml')}
          </button>
          <button
            type="button"
            className="copy btn-sm"
            onClick={() => save(tableXls(title, headers, rows), 'application/vnd.ms-excel', 'xls')}
          >
            {kitText('ui.export.xls')}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * Every element inside `root` that Tab can reach.
 *
 * Used only for the modal's own focus trap, so the sweep stays deliberately
 * plain: a real `tabIndex` order (anything positive) does not exist anywhere
 * in this kit, and `offsetParent` is `null` for anything `display: none` —
 * near enough to "visible" for a dialog that never hides part of itself.
 */
function focusablesIn(root: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => el.offsetParent !== null,
  );
}

/**
 * A form that interrupts, for the handful of actions the reference console shows as a
 * popup rather than a screen (Add Brand, Invite an affiliate) — closes on
 * Escape or a click on the backdrop, never on a click inside itself.
 */
export function Modal({
  title,
  onClose,
  children,
  wide,
  aside,
  keepOnBackdropClick,
  closeLabel,
  className,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Wider dialog for row detail with tables or several sections. */
  wide?: boolean;
  /** The close button's name when "Close" undersells it — the tour's "Exit tour". */
  closeLabel?: string;
  /** An extra class on the dialog itself, for a dialog with a layout of its own. */
  className?: string;
  /**
   * Actions on the dialog's own title bar, beside the close button — what
   * `Panel` already takes under the same name. For the action that belongs to
   * the whole dialog rather than to one section of it: a detail modal's
   * Retire, say, which in the body sat above the first panel looking like part
   * of it. Not for a form's Save, which belongs beside the form it saves.
   */
  aside?: ReactNode;
  /**
   * Stay open when the backdrop is clicked. For a dialog holding a form long
   * enough that losing it to a stray click is the expensive outcome — the
   * close button and Escape still close it.
   */
  keepOnBackdropClick?: boolean;
}) {
  const self = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const popupReturn = useRef<HTMLElement | null>(null);
  const titleId = useId();

  /*
   * The page behind does not scroll while a dialog is open.
   *
   * Reported 2026-09-29: scrolling a popup's table ran out of rows and carried
   * on scrolling the dashboard underneath, so closing the dialog left the
   * reader somewhere else entirely. The backdrop scrolls, the document does
   * not.
   *
   * Counted rather than toggled, because dialogs nest: a popup opened from a
   * popup would otherwise restore the page's scrolling the moment the inner
   * one closed, while the outer is still up. The last one out puts it back.
   *
   * The scrollbar it removes is replaced by padding of the same width, or the
   * whole page shifts sideways as the dialog opens — the layout jump that
   * makes a dialog feel like a page change.
   */
  useLayoutEffect(() => {
    const body = document.body;
    const depth = Number(body.dataset['modalDepth'] ?? '0') + 1;
    body.dataset['modalDepth'] = String(depth);
    if (depth === 1) {
      const gap = window.innerWidth - document.documentElement.clientWidth;
      body.dataset['modalOverflow'] = body.style.overflow;
      body.dataset['modalPadRight'] = body.style.paddingRight;
      body.style.overflow = 'hidden';
      if (gap > 0) body.style.paddingRight = `${gap}px`;
    }
    return () => {
      const left = Number(body.dataset['modalDepth'] ?? '1') - 1;
      if (left > 0) { body.dataset['modalDepth'] = String(left); return; }
      delete body.dataset['modalDepth'];
      body.style.overflow = body.dataset['modalOverflow'] ?? '';
      body.style.paddingRight = body.dataset['modalPadRight'] ?? '';
      delete body.dataset['modalOverflow'];
      delete body.dataset['modalPadRight'];
    };
  }, []);

  // The same "last `.modal-backdrop` in the document" test tells both this
  // and the Tab trap below which layer of a nested stack is on top.
  const isTopmost = () => {
    const all = document.querySelectorAll('.modal-backdrop');
    return all[all.length - 1] === self.current;
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A nested popup renders inside the one beneath it, so the last backdrop
      // in the document is the one on top — and only that one closes. Every
      // layer answering the same key shut the whole stack at once.
      if (isTopmost()) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * Focus goes in when the dialog opens and comes back out when it closes —
   * the keyboard half of a modal, same as the pointer half a click already
   * gets. The opener is whatever had focus the instant this mounted; a save
   * that unmounts its own trigger before this cleanup runs is why the return
   * checks the element is still in the document rather than just calling
   * `.focus()` on it.
   */
  useEffect(() => {
    const opener = document.activeElement;
    // Never onto a destructive control: a dialog that opens with focus on
    // "Reset authenticator" is one stray Enter from doing it.
    const firstInBody = bodyRef.current
      ? focusablesIn(bodyRef.current).find((el) => !el.matches('.danger, .danger-quiet, .link-button.danger'))
      : undefined;
    (firstInBody ?? dialogRef.current)?.focus();
    return () => {
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Tab/Shift+Tab cycle inside the topmost dialog only, so a click that opens
   * a second popup on top of this one hands the keyboard to that one rather
   * than leaving both trapping at once.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !isTopmost()) return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      /*
       * A calendar or a column menu opened from inside the dialog is drawn at
       * the end of <body>, outside it, and counted as outside it the trap sent
       * every Tab back to the dialog's start — so a date could not be picked
       * from the keyboard at all. An open one is part of the dialog: Tab goes
       * into it, and Tab past its last control comes back to where it left.
       */
      const popup = Array.from(document.querySelectorAll<HTMLElement>('.calendar, .column-menu-list'))
        .find((p) => p.getClientRects().length > 0);
      const inPopup = popup ? focusablesIn(popup) : [];
      const here = document.activeElement as HTMLElement | null;
      if (inPopup.length > 0 && here) {
        if (dialog.contains(here) && !e.shiftKey) {
          e.preventDefault();
          popupReturn.current = here;
          inPopup[0]!.focus();
          return;
        }
        const at = inPopup.indexOf(here);
        if (at !== -1) {
          e.preventDefault();
          const next = at + (e.shiftKey ? -1 : 1);
          if (next >= 0 && next < inPopup.length) inPopup[next]!.focus();
          else (popupReturn.current && document.contains(popupReturn.current) ? popupReturn.current : dialog).focus();
          return;
        }
      }
      const focusables = focusablesIn(dialog);
      if (focusables.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      const atStart = active === first || !dialog.contains(active);
      const atEnd = active === last || !dialog.contains(active);
      if (e.shiftKey ? atStart : atEnd) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="modal-backdrop" ref={self} onClick={keepOnBackdropClick ? undefined : onClose}>
      <section
        ref={dialogRef}
        className={`${wide ? 'modal modal-wide' : 'modal'}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 id={titleId}>{userText(title)}</h2>
          {aside ? <div className="modal-head-actions">{aside}</div> : null}
          <button
            type="button"
            className="modal-close"
            aria-label={closeLabel ?? kitText('action.close')}
            {...(closeLabel ? { title: closeLabel } : {})}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="body" ref={bodyRef}>{children}</div>
      </section>
    </div>
  );
}

/**
 * Item 13 (third CTO demo-call batch) — a deactivate/delete click gets a real
 * step in between, not `window.confirm` (the one place that pattern existed —
 * a reference-data row's Delete button — moves onto this too). Handles its
 * own busy/failure state so a caller drops it in without repeating that
 * plumbing for every destructive action it wraps.
 */
export function Confirm({
  title,
  body,
  confirmLabel = kitText('action.confirm'),
  tone = 'caution',
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  tone?: 'note' | 'caution' | 'error';
  /**
   * Whatever `onConfirm` does — call the API, close the modal, refresh the
   * parent's data — is entirely up to the caller. `Confirm` does not close
   * itself on success, since a caller that wants to show a result (a new
   * dispute id, a "carried forward" note) needs the dialog to stay put until
   * it says otherwise.
   */
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  return (
    // Escape, × and the backdrop do nothing while the request runs: closing
    // then left the request going with nowhere to show its failure.
    <Modal title={title} onClose={() => { if (!busy) onCancel(); }}>
      <div className="stack">
        {failure ? (
          <Callout tone="error">{explainFailure(failure).message}</Callout>
        ) : (
          // What confirming will do is the one thing here that must be read whole.
          <Callout tone={tone} fold={false}>{body}</Callout>
        )}
        <div className="form-actions">
          <button type="button" className="copy" disabled={busy} onClick={onCancel}>
            {kitText('action.cancel')}
          </button>
          <button
            type="button"
            className={tone === 'error' ? 'danger' : 'primary'}
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setFailure(null);
              Promise.resolve(onConfirm())
                .catch(setFailure)
                .finally(() => setBusy(false));
            }}
          >
            {busy ? kitText('action.working') : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * A note beside something, in the reader's own words.
 *
 * This took a `reference` and printed "Rule Book §2.5d" in a chip. The
 * reasoning was §0.8d — show the rule so somebody can quote it instead of
 * opening a ticket — and that is right for the people who have the Rule Book.
 * They are all on our side of it. An affiliate has never seen the document, so
 * the citation was a number they could not look up, attached to a sentence
 * that has to stand on its own anyway.
 *
 * The prop is removed rather than left unused, because an unused prop is one
 * somebody fills in again next month. Where a rule needs explaining, the
 * sentence explains it.
 */
export function Callout({
  tone = 'note',
  fold = true,
  children,
}: {
  tone?: 'note' | 'caution' | 'error';
  /**
   * False for a note that must be read whole: a result, a success, the body
   * of a confirmation. Only plain notes fold at all.
   */
  fold?: boolean;
  children: ReactNode;
}) {
  /*
   * A plain note shows two lines and offers the rest. Screens had grown three
   * and four standing paragraphs of explanation above the thing they explain,
   * which put the table a screen down; two lines is the gist, and the whole
   * sentence is one press away. A caution or an error is never folded — those
   * are the ones a reader must not skim past. The folded text stays in the
   * page, so a screen reader and find-in-page still reach all of it.
   */
  const text = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [open, setOpen] = useState(false);
  // Two notes never fold: one holding a control (a Copy button, a link, a
  // field), which could land on a hidden third line; and one inside a
  // <details> the reader has just opened to read it — folding that again
  // hid one paragraph behind two toggles.
  const [unfoldable, setUnfoldable] = useState(false);
  const folds = tone === 'note' && fold && !unfoldable;
  useLayoutEffect(() => {
    const el = text.current;
    if (el && (el.querySelector('a[href], button, input, select, textarea') || el.closest('details'))) {
      setUnfoldable(true);
      return;
    }
    if (!folds || open || !el) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, [folds, open, children]);

  return (
    // A screen reader announces an error the moment it appears rather than
    // only if the reader's cursor happens to land on it — a caution or a
    // plain note is not urgent the same way, so they stay unannounced.
    <p className={`callout ${tone}`} {...(tone === 'error' ? { role: 'alert' } : {})}>
      {/* The tone in a shape as well as a colour — a caution and an error are
          different sentences to somebody who cannot tell amber from red. */}
      <Icon name={`tone.${tone}`} className="callout-icon" />
      <span ref={text} className={folds && !open ? 'callout-text is-folded' : 'callout-text'}>
        {userTextNode(children)}
      </span>
      {folds && (overflows || open) ? (
        <button
          type="button"
          className="callout-more link-button"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {kitText(open ? 'ui.showLess' : 'ui.showMore')}
        </button>
      ) : null}
    </p>
  );
}

/**
 * A failed request, in one place: `explainFailure`'s sentence, and a way to
 * try again when the caller has one. No reference line — `explainFailure`
 * returns none, and the kit keeps rule identifiers off the screen
 * (`stripRuleReferences`), so a "Reference: …" slot could only ever be empty.
 */
export function ErrorNotice({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  const { message } = explainFailure(error);
  return (
    <div className="stack">
      <Callout tone="error">{message}</Callout>
      {onRetry ? (
        <button type="button" className="link-button" onClick={onRetry}>
          {kitText('ui.retry')}
        </button>
      ) : null}
    </div>
  );
}

export function Chip({
  tone,
  children,
}: {
  tone: 'neutral' | 'provisional' | 'locked' | 'overdue' | 'attention';
  children: ReactNode;
}) {
  return <span className={`chip ${tone}`}>{children}</span>;
}

export interface QueueItem {
  readonly key: string;
  /** Who or what — the affiliate, the invoice. */
  readonly title: string;
  /** What is being asked of the reader, in a line. */
  readonly detail: string;
  /** Where the decision is made. */
  readonly href: string;
  /** The kind of work, drawn beside the row so a mixed queue can be scanned. */
  readonly icon?: IconName;
}

/**
 * Work waiting for somebody, one row each, each row the link to where it is
 * decided.
 *
 * The dashboard used to give every item a card of its own with a Review
 * button in it: nine items were two screens of cards, every button said the
 * same word, and the thing to click was a 60px target inside a 150px card.
 * Here the whole row is the target and says what it opens.
 */
export function QueueList({
  items,
  max = items.length,
  more,
  onOpen,
}: {
  items: readonly QueueItem[];
  /** Rows shown before `more` takes over. */
  max?: number;
  /** The line under a list cut at `max` — how many more, and where they are. */
  more?: (hidden: number) => string;
  onOpen?: (item: QueueItem) => void;
}) {
  const shown = items.slice(0, max);
  const hidden = items.length - shown.length;
  return (
    <>
      <ul className="queue-list">
        {shown.map((item) => (
          <li key={item.key}>
            <a
              className="queue-item"
              href={item.href}
              {...(onOpen ? { onClick: () => onOpen(item) } : {})}
            >
              {item.icon ? <Icon name={item.icon} className="queue-icon" /> : null}
              <span className="queue-text">
                <span className="queue-title">{userText(item.title)}</span>
                <span className="queue-detail">{userText(item.detail)}</span>
              </span>
              <Icon name="open" size={16} className="queue-open" />
            </a>
          </li>
        ))}
      </ul>
      {hidden > 0 && more ? <p className="queue-more">{userText(more(hidden))}</p> : null}
    </>
  );
}

export function TableScroll({ children }: { children: ReactNode }) {
  return <div className="table-scroll">{children}</div>;
}

/** See `row-open.ts` — kept there so it can be unit-tested without a DOM. */
export { rowOpen } from './row-open.ts';

/**
 * A column heading that is also the control for that column.
 *
 * A panel header carrying three loose dropdowns says nothing about which
 * column each one acts on — you read "By commission" and have to go looking
 * for the commission column to confirm what it reordered. Hanging the control
 * off the heading itself removes the guess: the arrow beside COMMISSION is
 * what ranks by commission, the arrow beside AFFILIATE is what narrows to one.
 *
 * `active` is not decoration. A menu that is currently narrowing the table has
 * to look different from one that is not, or a filtered table reads as an
 * empty one.
 */
export function ColumnMenu({
  label,
  active = false,
  align = 'start',
  children,
}: {
  label: ReactNode;
  active?: boolean;
  /** Which edge the popover hangs from — `end` for right-aligned numeric columns. */
  align?: 'start' | 'end';
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [menuBox, setMenuBox] = useState<{ top: number; left: number; maxHeight: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 4;
      const width = 15 * 16; // matches .column-menu-list min-width
      const preferred = 18 * 16;
      const spaceBelow = window.innerHeight - r.bottom - gap;
      const spaceAbove = r.top - gap;
      const openDown = spaceBelow >= Math.min(preferred, 9 * 16) || spaceBelow >= spaceAbove;
      const maxHeight = Math.max(6 * 16, Math.min(preferred, openDown ? spaceBelow : spaceAbove));
      // Hung from the heading's right edge for a right-aligned column, so a
      // menu on the last column cannot open off the side of the screen.
      const rawLeft = align === 'end' ? r.right - width : r.left;
      const left = Math.max(8, Math.min(rawLeft, window.innerWidth - width - 8));
      setMenuBox({ top: openDown ? r.bottom + gap : r.top - gap - maxHeight, left, maxHeight });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = wrapRef.current;
      const target = e.target as Node | null;
      if (el && target && el.contains(target)) return;
      // The popover is portalled out of the table, so containment in the
      // trigger is not enough to tell a click inside it from a click away.
      if (target instanceof Element && target.closest('.column-menu-list')) return;
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="column-menu" ref={wrapRef}>
      <button
        type="button"
        className={active ? 'column-menu-trigger is-active' : 'column-menu-trigger'}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="column-menu-label">{userTextNode(label)}</span>
        <span className="column-menu-caret" aria-hidden="true" />
      </button>
      {open && menuBox
        ? createPortal(
            <div
              className="column-menu-list"
              role="menu"
              style={{ top: menuBox.top, left: menuBox.left, maxHeight: menuBox.maxHeight }}
            >
              {children(close)}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/**
 * A column heading that orders the table by its own column.
 *
 * One click, not a menu: the arrow beside a heading is read as "sort by this",
 * and a heading that answered that click with a popover containing a single
 * item was a menu standing in for the click it was already being given. The
 * caret points the way the rows now run, so the direction is on screen rather
 * than remembered, and `aria-sort` says the same thing to a screen reader.
 *
 * Clicking the column that is already sorted turns it round; clicking another
 * starts it descending, because the first question asked of a ranked table is
 * always "who is at the top".
 */
export function SortableTh({
  label,
  sorted,
  direction,
  numeric = false,
  onSort,
}: {
  label: ReactNode;
  /** Whether the table is currently ordered by this column. */
  sorted: boolean;
  /** The direction this column runs in when it is the sorted one. */
  direction: 'asc' | 'desc';
  /** Right-aligned, for a column of figures. */
  numeric?: boolean;
  onSort: (direction: 'asc' | 'desc') => void;
}) {
  const next = sorted && direction === 'desc' ? 'asc' : 'desc';
  const caret = ['column-menu-caret'];
  if (sorted && direction === 'asc') caret.push('is-asc');

  return (
    <th
      {...(numeric ? { className: 'num' } : {})}
      aria-sort={sorted ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="column-menu">
        <button
          type="button"
          className={sorted ? 'column-menu-trigger is-active' : 'column-menu-trigger'}
          onClick={() => onSort(next)}
        >
          <span className="column-menu-label">{userTextNode(label)}</span>
          <span className={caret.join(' ')} aria-hidden="true" />
        </button>
      </div>
    </th>
  );
}

/** One line of a {@link ColumnMenu}. `selected` marks the state it would set as the state you are in. */
export function ColumnMenuItem({
  selected = false,
  onClick,
  children,
}: {
  selected?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      className={selected ? 'column-menu-item is-selected' : 'column-menu-item'}
      onClick={onClick}
    >
      {userTextNode(children)}
    </button>
  );
}

/** A heading inside a {@link ColumnMenu} — "Rank by", "Narrow to". */
export function ColumnMenuGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="column-menu-group">
      <p className="column-menu-group-label">{userText(label)}</p>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

/**
 * A placeholder with the shape of the thing arriving.
 *
 * "Loading" and "loaded but empty" must not look alike: an affiliate shown an
 * empty table while the request is still in flight concludes they earned
 * nothing.
 */
export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="rows" aria-busy="true" aria-label={kitText('ui.loading')}>
      {Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton" />)}
    </div>
  );
}

/**
 * A labelled control.
 *
 * The label is a real `<label>` bound to the field, not text beside it: a
 * filter whose label is decorative is one a screen reader user cannot use, and
 * one that does not focus the field when clicked.
 *
 * `group` is for a field holding several controls — a checkbox list, a set of
 * radios, rows of inputs. A `<label>` hands its clicks to the first control
 * inside it, so every click in such a field that missed a row — its heading,
 * its hint, the gap between rows — ticked the first box. A group still names
 * its controls for a screen reader, without taking their clicks.
 *
 * `action` is the control that edits what the field displays, on the label's
 * own line. Read-only fields that had one were putting it in a `.form-actions`
 * row at the foot of the panel, which is the right place for "save this form"
 * and the wrong one for "edit that list" — by the time a reader reaches the
 * bottom they have scrolled past the thing the button acts on, and a panel
 * with two such lists has two buttons down there naming neither.
 *
 * It sits outside the `<label>` element, never inside: a `<label>` hands its
 * clicks to the control it names, and a button that also fires the field
 * beneath it is the `group` bug above wearing a different hat.
 */
export function Field({
  label,
  hint,
  error,
  group,
  action,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  group?: boolean;
  /** A control that edits this field's contents, shown beside the label. */
  action?: ReactNode;
  children: ReactNode;
}) {
  const className = `field${error ? ' has-error' : ''}`;
  const heading = action ? (
    <span className="field-label-row">
      <span className="field-label">{userText(label)}</span>
      <span className="field-action">{action}</span>
    </span>
  ) : (
    <span className="field-label">{userText(label)}</span>
  );
  const rest = (
    <>
      {error ? <span className="field-error">{userText(error)}</span> : null}
      {hint && !error ? <span className="field-hint">{userText(hint)}</span> : null}
    </>
  );
  if (group) {
    return (
      <div className={className} role="group" aria-label={userText(label)}>
        {heading}{children}{rest}
      </div>
    );
  }
  // With an action the wrapper cannot be the `<label>` any more, so the label
  // binds to the control alone and the action stays outside it. Its visible
  // text lives in `heading`, outside this label, so the label needs its own
  // copy of that text — a screen-reader-only span, the same "name from
  // content" a plain `<label>` gets from wrapping visible text, which the
  // plain branch below already relies on. Hint/error move inside the label
  // too (they used to sit after it, reaching neither the name nor any
  // description) rather than wired up with `aria-describedby`: the controls
  // Field wraps (`TextInput` et al.) accept a fixed, named set of props and
  // do not forward unlisted ARIA attributes onto their `<input>`, so a prop
  // injected here would silently go nowhere.
  return action ? (
    <div className={className}>
      {heading}
      <label className="field-bound">
        <span className="sr-only">{userText(label)}</span>
        {children}
        {rest}
      </label>
    </div>
  ) : (
    <label className={className}>{heading}{children}{rest}</label>
  );
}

/**
 * Period / brand / operator filter rows. Uses the same label–control–hint grid
 * as every other horizontal field row so siblings stay aligned when only some
 * carry a hint underneath.
 */
/**
 * A row of views on the same data, and the actions that are not views.
 *
 * These were a run of identical outline buttons with `aria-current` on the
 * chosen one — which nothing styled, so the chosen view looked exactly like
 * the two you were not in, and the button that opened a settings panel looked
 * exactly like a third view. The tabs are now one segmented control that
 * shows which segment you are in, and anything that is not a view sits apart
 * from them on the other side of the row.
 */
export function TabRow({ children }: { children: ReactNode }) {
  return <div className="tab-row">{children}</div>;
}

export function TabBar({ children, 'aria-label': ariaLabel }: { children: ReactNode; 'aria-label'?: string }) {
  return (
    <div className="tab-bar" role="tablist" {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}>
      {children}
    </div>
  );
}

export function Tab({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      className={selected ? 'tab is-selected' : 'tab'}
      onClick={onClick}
    >
      {userTextNode(children)}
    </button>
  );
}

/** The right-hand end of a {@link TabRow} — actions, not views. */
export function TabRowActions({ children }: { children: ReactNode }) {
  return <div className="tab-row-actions">{children}</div>;
}

export function FilterBar({ children }: { children: ReactNode }) {
  return <div className="filters-row report-toolbar">{children}</div>;
}

/**
 * A filter-as-you-type picker. Shows names, stores ids — so an operator is not
 * asked to paste ULIDs from somewhere else.
 *
 * The options list is portalled to `document.body` when open: a modal's own
 * `overflow-y: auto` (and the backdrop's) would otherwise clip an absolutely
 * positioned list that opens below the field — "Raise a statement" and every
 * other dialog with a long affiliate/period list.
 */
export function SearchSelect({
  value,
  onChange,
  options,
  placeholder = kitText('ui.search.type'),
  clearOnSelect,
}: {
  value: string;
  onChange: (v: string) => void;
  /**
   * `disabled` is shown and refused, never hidden. A name missing from a list
   * reads as a bug in the list; a name that is there, greyed, and says why is
   * an answer to the question the reader was about to ask.
   */
  options: readonly { value: string; label: string; disabled?: boolean }[];
  placeholder?: string;
  /**
   * For a field that collects several answers: picking one empties the box and
   * leaves the list open for the next, instead of filling the box with what was
   * just chosen and making it something to delete by hand.
   */
  clearOnSelect?: boolean;
}) {
  const [query, setQuery] = useState('');
  /*
   * Whether the box holds something somebody typed, or the label of what is
   * already chosen. Only the first of those is a search.
   *
   * Without this, opening a field that has a value filtered the list by that
   * value's own label, so a timezone reading UTC offered exactly one option —
   * UTC — and a currency reading USD offered only USD. The control looked like
   * a list with one entry rather than a list already scrolled to your answer.
   */
  const [typing, setTyping] = useState(false);
  const [open, setOpen] = useState(false);
  /*
   * The option the keyboard is on. Focus stays in the box the whole time — the
   * combobox pattern — and the screen reader follows `aria-activedescendant`.
   * Tabbing into the list could never have worked: the box closes the list
   * 150ms after it loses focus, so the list unmounted under the focus that
   * moved into it, and a keyboard user had no way to choose at all.
   */
  const [active, setActive] = useState(-1);
  const listId = useId();
  const optionId = (i: number) => `${listId}-o${i}`;
  const wrapRef = useRef<HTMLDivElement>(null);
  const [menuBox, setMenuBox] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const selected = options.find((o) => o.value === value);
  const needle = typing ? query.trim().toLowerCase() : '';
  const filtered = visibleOptions(options, query, typing);

  // A new list, or a closed one, starts with nothing highlighted.
  useEffect(() => { setActive(-1); }, [open, query]);
  useEffect(() => {
    if (active >= 0) document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const choose = (o: { value: string; label: string; disabled?: boolean }) => {
    if (o.disabled === true) return;
    onChange(o.value);
    setTyping(false);
    setQuery(clearOnSelect ? '' : o.label);
    if (!clearOnSelect) setOpen(false);
  };
  /** The next option in `by`'s direction that can be chosen, or `from` if none. */
  const step = (from: number, by: 1 | -1) => {
    for (let i = from + by; i >= 0 && i < filtered.length; i += by) {
      if (filtered[i]?.disabled !== true) return i;
    }
    return from;
  };

  useEffect(() => {
    // `value === ''` is how callers spell "nothing chosen" (a leading
    // { value: '', label: 'Choose…' } / 'All brands' option carries that same
    // sentinel). Syncing query to *that* option's label pre-fills the search
    // box with "Choose…", and typing then filters every real option out
    // because none of their labels contain it — the box looks empty. Leaving
    // query blank here shows `placeholder` instead and searches from empty.
    // Not while somebody is mid-word: typing over a chosen value clears it
    // (below), and syncing on that clear would delete the character that
    // caused it, so the field could never be retyped.
    if (typing) return;
    setQuery(value && selected ? selected.label : '');
  }, [value, selected, typing]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    let broughtIntoView = false;
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      let r = el.getBoundingClientRect();
      const gap = 4;
      const preferred = 12 * 16; // 12rem — matches .search-select-list max-height
      if (!broughtIntoView && window.innerHeight - r.bottom - gap < preferred) {
        // Once per opening: a menu drawn upward covers the fields above the one
        // being answered, which reads as a list belonging to the wrong field.
        broughtIntoView = true;
        el.scrollIntoView({ block: 'center' });
        r = el.getBoundingClientRect();
      }
      const spaceBelow = window.innerHeight - r.bottom - gap;
      const spaceAbove = r.top - gap;
      const openDown = spaceBelow >= Math.min(preferred, 8 * 16) || spaceBelow >= spaceAbove;
      const maxHeight = Math.max(6 * 16, Math.min(preferred, openDown ? spaceBelow : spaceAbove));
      setMenuBox({
        top: openDown ? r.bottom + gap : r.top - gap - maxHeight,
        left: r.left,
        width: r.width,
        maxHeight,
      });
    };
    place();
    window.addEventListener('resize', place);
    // Capture scroll from the modal body / backdrop, not only the window.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, filtered.length, query]);

  const menu =
    open && menuBox && filtered.length > 0 ? (
      <ul
        id={listId}
        className="search-select-list search-select-list-portal"
        role="listbox"
        style={{
          top: menuBox.top,
          left: menuBox.left,
          width: menuBox.width,
          maxHeight: menuBox.maxHeight,
        }}
      >
        {filtered.map((o, i) => (
          <li key={o.value} role="presentation">
            <button
              type="button"
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              tabIndex={-1}
              disabled={o.disabled === true}
              className={[o.value === value ? 'is-selected' : '', i === active ? 'is-active' : ''].filter(Boolean).join(' ') || undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(o)}
            >
              {o.label}
            </button>
          </li>
        ))}
      </ul>
    ) : open && menuBox && filtered.length === 0 && needle !== '' ? (
      <p
        className="search-select-empty search-select-list-portal"
        style={{
          top: menuBox.top,
          left: menuBox.left,
          width: menuBox.width,
        }}
      >
        {kitText('ui.noMatches')}
      </p>
    ) : null;

  return (
    <div className="search-select" ref={wrapRef}>
      <input
        className="control"
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        {...(open && active >= 0 ? { 'aria-activedescendant': optionId(active) } : {})}
        aria-autocomplete="list"
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setTyping(true);
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange('');
        }}
        onFocus={(e) => {
          setOpen(true);
          setTyping(false);
          // The whole answer is selected, so the first keystroke replaces it
          // rather than appending to it.
          e.target.select();
        }}
        onBlur={() => {
          // A dialog's autofocus can bounce focus off this input and
          // straight back (mount → focus → blur → focus) before this fires.
          // Closing unconditionally then closed a list the second focus had
          // just reopened, and a click on an input that is already focused
          // raises no further focus event to reopen it — the list stayed
          // shut until the field was tabbed away from and back. Checked at
          // fire time, not scheduled time: only close when focus has really
          // left the wrapper, which still lets an outside click close it.
          setTimeout(() => {
            if (wrapRef.current?.contains(document.activeElement)) return;
            setOpen(false);
            setTyping(false);
          }, 150);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) { setOpen(true); return; }
            setActive((a) => (e.key === 'ArrowDown' ? step(a, 1) : step(a < 0 ? filtered.length : a, -1)));
          } else if (e.key === 'Enter' && open && active >= 0 && filtered[active]) {
            e.preventDefault();
            choose(filtered[active]);
          } else if (e.key === 'Escape' && open) {
            // Closes the list, not the dialog the field is in.
            e.stopPropagation();
            setOpen(false);
          }
        }}
      />
      {menu ? createPortal(menu, document.body) : null}
    </div>
  );
}

/**
 * Long lists become searchable; short ones stay a native select.
 *
 * Twelve, not five. A native dropdown of a dozen options is read in one
 * glance and needs one click; a search box asks somebody to type a word
 * before it will show them what there is. Five turned genuinely short,
 * fixed lists — the seven ways to sort an affiliate list, the currencies
 * an operator keeps books in — into type-to-search controls whose answers
 * are not words anyone would think to type. That six call sites already
 * pass `searchable={false}` by hand is the same complaint, made one screen
 * at a time.
 *
 * Above a dozen, scanning costs more than typing, which is where the
 * country and timezone lists live.
 */
const SEARCHABLE_SELECT_THRESHOLD = 12;

export function Select<T extends string>({
  value,
  onChange,
  options,
  placeholder,
  searchable,
}: {
  value: T;
  onChange: (v: T) => void;
  options: readonly { value: T; label: string; disabled?: boolean }[];
  placeholder?: string;
  /** When false, always use a native select — better for short filter lists. */
  searchable?: boolean;
}) {
  const useSearch =
    searchable ?? options.length > SEARCHABLE_SELECT_THRESHOLD;
  if (useSearch) {
    return (
      <SearchSelect
        value={value}
        onChange={(v) => onChange(v as T)}
        options={options}
        placeholder={placeholder ?? kitText('ui.search.ellipsis')}
      />
    );
  }

  return (
    <select className="control" value={value} onChange={(e) => onChange(e.target.value as T)}>
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled === true}>{o.label}</option>
      ))}
    </select>
  );
}

/**
 * Landing pages live on the brand, not the campaign. One page → show it; several →
 * pick; none → explain the brand-default redirect.
 */
export function LinkLandingPageField({
  pages,
  value,
  onChange,
}: {
  pages: readonly { landing_page_id: string; label: string; url: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  useEffect(() => {
    if (pages.length === 1) onChange(pages[0]!.landing_page_id);
    else if (pages.length === 0) onChange('');
  }, [pages, onChange]);

  if (pages.length === 0) {
    return (
      <Field
        label={kitText('ui.landing')}
        hint={kitText('ui.landing.none')}
      >
        <span className="faint">—</span>
      </Field>
    );
  }

  if (pages.length === 1) {
    const page = pages[0]!;
    return (
      <Field label={kitText('ui.landing')} hint={kitText('ui.landing.hint')}>
        <p className="link-note landing-note">
          {page.label} <span className="faint">({page.url})</span>
        </p>
      </Field>
    );
  }

  return (
    <Field label={kitText('ui.landing')} hint={kitText('ui.landing.pick')}>
      <Select
        value={value}
        onChange={onChange}
        options={[
          { value: '', label: kitText('ui.brandDefault') },
          ...pages.map((p) => ({ value: p.landing_page_id, label: p.label })),
        ]}
      />
    </Field>
  );
}

/** Clipboard API first; textarea fallback when permissions or context block it. */
export async function copyText(text: string): Promise<boolean> {
  if (text === '') return false;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through — staging HTTP, iframe embeds, or denied permission.
    }
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', 'true');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function CopyButton({ text, label = kitText('ui.copy') }: { text: string; label?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');

  return (
    <button
      type="button"
      className="copy"
      onClick={() => {
        void copyText(text).then((ok) => {
          setState(ok ? 'copied' : 'failed');
          window.setTimeout(() => setState('idle'), 1600);
        });
      }}
    >
      {state === 'copied'
        ? kitText('ui.copied')
        : state === 'failed'
          ? kitText('ui.copyFailed')
          : label}
    </button>
  );
}

/**
 * A short value copied by clicking anywhere on it — a table cell this narrow
 * has no room for a code block and a separate "Copy" button beside it. The
 * icon is the only chrome, and doubles as the feedback once the copy lands.
 */
export function CopyableCode({
  text,
  label = kitText('ui.copy'),
  multiline = false,
}: {
  text: string;
  label?: string;
  multiline?: boolean;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const title = state === 'copied'
    ? kitText('ui.copied')
    : state === 'failed'
      ? kitText('ui.copyFailed')
      : label;

  return (
    <button
      type="button"
      className={`copyable-code${multiline ? ' is-multiline' : ''}${state === 'copied' ? ' is-copied' : ''}`}
      title={title}
      aria-label={title}
      onClick={() => {
        void copyText(text).then((ok) => {
          setState(ok ? 'copied' : 'failed');
          window.setTimeout(() => setState('idle'), 1600);
        });
      }}
    >
      <code>{text}</code>
      <Icon name={state === 'copied' ? 'copy.done' : 'copy'} className="copyable-code-icon" />
    </button>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
  invalid,
  onKeyDown,
  autoFocus,
  'aria-label': ariaLabel,
  autoCapitalize = 'off',
  autoCorrect = 'off',
  spellCheck = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  /** For a box that a disclosure just revealed: opening one and leaving the
   *  caret where it was makes the reader hunt for what they asked for. */
  autoFocus?: boolean;
  /** For a box that stands outside a `Field` — a placeholder is a hint that
   *  leaves as soon as you type, not the box's name. */
  'aria-label'?: string;
  /** Safari defaults to sentence case after “.” — off for codes, domains and names typed literally. */
  autoCapitalize?: 'off' | 'none' | 'on' | 'sentences' | 'words' | 'characters';
  autoCorrect?: 'off' | 'on';
  spellCheck?: boolean;
}) {
  return (
    <input
      className="control"
      type="text"
      value={value}
      placeholder={placeholder ?? ''}
      aria-invalid={invalid ? true : undefined}
      autoFocus={autoFocus ?? false}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      autoCapitalize={autoCapitalize}
      autoCorrect={autoCorrect}
      spellCheck={spellCheck}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
    />
  );
}

/** A date typed freehand ("2026-2-2", "02/02/26"…) is a date `new Date()` cannot
 *  trust — the browser's own picker only ever produces a real one. */
/* ------------------------------------------------------------------ *
 * Dates
 *
 * The browser's own date picker cannot be styled — `accent-color` reaches the
 * selected day and nothing else reaches the rest — so it arrived as a white
 * Chrome panel in the middle of a night desk. This draws the calendar itself,
 * from the same tokens as everything around it.
 *
 * Days are built and read in UTC. A grid built in local time puts the 1st of
 * the month in the previous month for every reader east of Greenwich after
 * lunch, which is the kind of bug that only shows up in someone else's
 * timezone.
 * ------------------------------------------------------------------ */

export function DateInput({
  value,
  onChange,
  invalid,
  min,
  max,
  today: todayOverride,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
  /** Earliest selectable date, as YYYY-MM-DD. */
  min?: string;
  /** Latest selectable date, as YYYY-MM-DD. */
  max?: string;
  /**
   * Whose "today", as YYYY-MM-DD, for the Today button and the marked day.
   * Defaults to the device's, which is what every screen wants since
   * 2026-10-12: a picked day is the reader's own, and the screen converts
   * its midnight to UTC (`localDayStart`) before sending it.
   */
  today?: string;
}) {
  const locale = displayLocale();
  const today = todayOverride && /^\d{4}-\d{2}-\d{2}$/.test(todayOverride) ? todayOverride : todayIso();
  const [open, setOpen] = useState(false);
  const opened = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : today;
  const [cursor, setCursor] = useState(() => ({
    year: dayFrom(opened).getUTCFullYear(),
    month: dayFrom(opened).getUTCMonth(),
  }));
  const wrapRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ top: number; left: number } | null>(null);

  // Reopening on a date chosen elsewhere shows that date's month, not the one
  // left behind by the last time this was open.
  useEffect(() => {
    if (!open) return;
    setCursor({ year: dayFrom(opened).getUTCFullYear(), month: dayFrom(opened).getUTCMonth() });
  }, [open, opened]);

  useLayoutEffect(() => {
    if (!open) {
      setBox(null);
      return;
    }
    const place = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 4;
      const height = 20 * 16;
      const below = window.innerHeight - r.bottom - gap;
      setBox({
        top: below >= height ? r.bottom + gap : Math.max(gap, r.top - gap - height),
        left: Math.min(r.left, Math.max(gap, window.innerWidth - 19 * 16)),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  // Escape closes it, and so does a click anywhere that is not the calendar or
  // the field that opened it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (wrapRef.current?.contains(target ?? null)) return;
      if (target instanceof Element && target.closest('.calendar')) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const monthName = new Intl.DateTimeFormat(locale, {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(Date.UTC(cursor.year, cursor.month, 1)));
  // Any week does for the names, and this grid's first row starts on a Sunday
  // by construction — the same day the grid below starts on.
  const weekdays = monthGrid(cursor.year, cursor.month).slice(0, 7).map((iso) =>
    new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' }).format(dayFrom(iso)),
  );
  const days = monthGrid(cursor.year, cursor.month);
  const shown = value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(dayFrom(value))
    : kitText('ui.date.choose');

  const calendar = open && box ? (
    <div
      className="calendar"
      role="dialog"
      aria-label={kitText('ui.date.calendar')}
      style={{ top: box.top, left: box.left }}
    >
      <div className="calendar-head">
        <button
          type="button"
          aria-label={kitText('ui.date.previousMonth')}
          onClick={() => setCursor(shiftMonth(cursor.year, cursor.month, -1))}
        >
          ‹
        </button>
        <span className="calendar-month">{monthName}</span>
        <button
          type="button"
          aria-label={kitText('ui.date.nextMonth')}
          onClick={() => setCursor(shiftMonth(cursor.year, cursor.month, 1))}
        >
          ›
        </button>
      </div>
      <div className="calendar-weekdays" aria-hidden="true">
        {weekdays.map((w, i) => <span key={i}>{w}</span>)}
      </div>
      <div className="calendar-grid">
        {days.map((iso) => {
          const d = dayFrom(iso);
          const outside = d.getUTCMonth() !== cursor.month;
          const allowed = dayInRange(iso, min, max);
          const classes = [
            'calendar-day',
            outside ? 'is-outside' : '',
            iso === today ? 'is-today' : '',
            iso === value ? 'is-picked' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={iso}
              type="button"
              className={classes}
              disabled={!allowed}
              aria-current={iso === today ? 'date' : undefined}
              aria-pressed={iso === value}
              onClick={() => {
                onChange(iso);
                setOpen(false);
              }}
            >
              {d.getUTCDate()}
            </button>
          );
        })}
      </div>
      <div className="calendar-foot">
        <button
          type="button"
          disabled={!dayInRange(today, min, max)}
          onClick={() => {
            onChange(today);
            setOpen(false);
          }}
        >
          {kitText('ui.date.today')}
        </button>
        <button
          type="button"
          onClick={() => {
            onChange('');
            setOpen(false);
          }}
        >
          {kitText('ui.date.clear')}
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="date-field" ref={wrapRef}>
      <button
        type="button"
        className="control date-field-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid ? true : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? undefined : 'faint'}>{shown}</span>
        <span className="date-field-icon" aria-hidden="true">▤</span>
      </button>
      {calendar ? createPortal(calendar, document.body) : null}
    </div>
  );
}

/** A typed or pasted "%" is redundant — the suffix already says it. */
function stripPercentSign(value: string): string {
  return value.replace(/%/g, '');
}

/** A rate field with a fixed "%" suffix, so the unit is shown rather than typed. */
export function PercentInput({
  value,
  onChange,
  placeholder,
  invalid,
  onKeyDown,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className={`percent-input${invalid ? ' is-invalid' : ''}`}>
      <input
        className="control percent-input-field"
        type="text"
        inputMode="decimal"
        value={value}
        placeholder={placeholder ?? ''}
        aria-invalid={invalid ? true : undefined}
        onChange={(e) => onChange(stripPercentSign(e.target.value))}
        onKeyDown={onKeyDown}
      />
      <span className="percent-input-suffix" aria-hidden="true">%</span>
    </div>
  );
}

/** Accidental spaces/tabs from typing or paste — stripped on every change. */
function stripEmailWhitespace(value: string): string {
  return value.replace(/\s/g, '');
}

/**
 * A file picker that belongs to this console rather than to the browser.
 *
 * A bare `input[type=file]` paints its own button and its own "no file
 * selected", in the browser's colours, its own font and the browser's
 * language — which on a dark screen reads as a piece of some other
 * application that wandered in. Only the painting is replaced here: the
 * input is still in the page, still focusable, still what opens the picker
 * and still what announces the chosen file. Swapping it for a div that
 * calls `.click()` would look the same and lose both the keyboard and the
 * screen reader, which is the trade almost every version of this makes.
 */
export function FileInput({
  accept,
  fileName,
  onChoose,
  buttonLabel = kitText('ui.file.choose'),
}: {
  accept?: string;
  /** The chosen file's name, or '' when nothing is chosen yet. */
  fileName: string;
  onChoose: (file: File | null) => void;
  buttonLabel?: string;
}) {
  return (
    <label className="file-input">
      <input
        className="file-input-native"
        type="file"
        {...(accept ? { accept } : {})}
        onChange={(e) => onChoose(e.target.files?.[0] ?? null)}
      />
      <span className="file-input-button">{buttonLabel}</span>
      <span className={fileName ? 'file-input-name' : 'file-input-name is-empty'}>
        {fileName || kitText('ui.file.none')}
      </span>
    </label>
  );
}

/** §33.4 — a file sent with a request, as base64 inside the JSON body. */
export interface AttachmentUpload {
  readonly fileName: string;
  readonly contentType: string;
  readonly dataBase64: string;
}

/** One file on a dispute, as the API lists it: its name and size, never its content. */
export interface DisputeEvidenceItem {
  readonly id: string;
  readonly file_name: string;
  readonly content_type: string;
  readonly size_bytes: number;
  readonly uploaded_by_role: 'AFFILIATE' | 'OPERATOR';
  readonly for_status: string;
  readonly uploaded_at: string;
}

/*
 * What api-core's dispute-evidence.ts accepts, restated here so a file the
 * API would refuse is refused before it is read into memory and sent. The API
 * still checks every byte; this only saves somebody a 25 MB upload to learn
 * that a .docx is not on the list.
 */
const ATTACHMENT_TYPES_BY_EXTENSION: Readonly<Record<string, string>> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp',
  pdf: 'application/pdf', mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime',
  csv: 'text/csv', txt: 'text/plain',
};
const ACCEPTED_ATTACHMENT_TYPES = new Set(Object.values(ATTACHMENT_TYPES_BY_EXTENSION));
const ATTACHMENT_ACCEPT = [
  ...Object.keys(ATTACHMENT_TYPES_BY_EXTENSION).map((ext) => `.${ext}`),
  ...ACCEPTED_ATTACHMENT_TYPES,
].join(',');
const ATTACHMENT_MAX_FILES = 5;
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_MAX_TOTAL_BYTES = 25 * 1024 * 1024;

/**
 * The type a file is sent as: the browser's own answer when it is one the
 * API takes, the extension otherwise. Windows reports a CSV as
 * application/vnd.ms-excel, and some browsers report nothing at all for one.
 */
function attachmentType(file: File): string | null {
  if (ACCEPTED_ATTACHMENT_TYPES.has(file.type)) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ATTACHMENT_TYPES_BY_EXTENSION[ext] ?? null;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error(kitText('ui.attach.unreadable', { name: file.name })));
    reader.readAsDataURL(file);
  });
}

/** The chosen files, ready for a request body. */
export function toAttachmentUploads(files: readonly File[]): Promise<AttachmentUpload[]> {
  return Promise.all(
    files.map(async (file) => ({
      fileName: file.name,
      contentType: attachmentType(file) ?? file.type,
      dataBase64: await readAsBase64(file),
    })),
  );
}

/**
 * Several files, chosen one at a time and removable before sending. Built on
 * FileInput, so each pick is still the real input with its keyboard and
 * screen-reader behaviour; the input is remounted after every pick so that
 * choosing a file removed a moment ago counts as a change again.
 */
export function AttachmentPicker({
  files,
  onChange,
}: {
  files: readonly File[];
  onChange: (files: File[]) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);
  const [pick, setPick] = useState(0);
  const total = files.reduce((n, f) => n + f.size, 0);

  const add = (file: File | null) => {
    setPick((n) => n + 1);
    if (!file) return;
    const refused =
      !attachmentType(file) ? kitText('ui.attach.type', { name: file.name })
      : file.size === 0 ? kitText('ui.attach.empty', { name: file.name })
      : file.size > ATTACHMENT_MAX_BYTES ? kitText('ui.attach.tooLarge', { name: file.name })
      : total + file.size > ATTACHMENT_MAX_TOTAL_BYTES ? kitText('ui.attach.tooMuch')
      : null;
    setProblem(refused);
    if (!refused) onChange([...files, file]);
  };

  return (
    <div className="attachment-picker">
      {files.length > 0 ? (
        <ul className="attachment-list">
          {files.map((f, i) => (
            <li key={`${i}-${f.name}`}>
              <span className="attachment-name">{f.name}</span>
              <span className="faint">{formatFileSize(f.size)}</span>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setProblem(null);
                  onChange(files.filter((_, j) => j !== i));
                }}
              >
                {kitText('ui.attach.remove')}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {files.length < ATTACHMENT_MAX_FILES ? (
        <FileInput
          key={pick}
          accept={ATTACHMENT_ACCEPT}
          fileName={files.length > 0 ? kitText('ui.attach.count', { n: files.length }) : ''}
          buttonLabel={kitText(files.length > 0 ? 'ui.attach.another' : 'ui.attach.add')}
          onChoose={add}
        />
      ) : null}
      {problem ? (
        <Callout tone="error">{problem}</Callout>
      ) : (
        <span className="field-hint">{kitText('ui.attach.hint')}</span>
      )}
    </div>
  );
}

/**
 * The files already on a dispute. Opening one fetches it with the session
 * and shows it in a new tab (HttpClient.openInNewTab) — a plain link would
 * carry no credentials.
 */
/**
 * A dispute's attachments as thumbnails, at most two tiles wide (owner,
 * 2026-10-07): with two or fewer, each one; with more, the first and a "+N"
 * tile for the rest. An image is drawn from the file itself, fetched with the
 * session; anything else, or an image that will not load, is a tile naming
 * its type. A tile opens the file; "+N" opens wherever the rest are listed.
 */
export function EvidenceThumbs({
  items,
  load,
  onOpen,
  onMore,
  label,
}: {
  items: readonly DisputeEvidenceItem[];
  load: (item: DisputeEvidenceItem) => Promise<Blob>;
  onOpen: (item: DisputeEvidenceItem) => void;
  onMore: () => void;
  /** Accessible name for the group, e.g. "2 files attached". */
  label: string;
}) {
  if (items.length === 0) return null;
  const shown = items.length <= 2 ? items : items.slice(0, 1);
  const rest = items.length - shown.length;
  return (
    <div className="evidence-thumbs" role="group" aria-label={label}>
      {shown.map((item) => (
        <EvidenceThumb key={item.id} item={item} load={load} onOpen={() => onOpen(item)} />
      ))}
      {rest > 0 ? (
        <button type="button" className="evidence-thumb evidence-thumb-more" onClick={onMore}>
          +{rest}
        </button>
      ) : null}
    </div>
  );
}

function EvidenceThumb({ item, load, onOpen }: {
  item: DisputeEvidenceItem;
  load: (item: DisputeEvidenceItem) => Promise<Blob>;
  onOpen: () => void;
}) {
  const isImage = item.content_type.startsWith('image/');
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!isImage) return;
    let live = true;
    let href: string | null = null;
    load(item).then(
      (blob) => {
        if (!live) return;
        href = URL.createObjectURL(blob);
        setSrc(href);
      },
      () => { /* the file tile below stands in for it */ },
    );
    return () => {
      live = false;
      if (href) URL.revokeObjectURL(href);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);
  const ext = (item.file_name.split('.').pop() ?? '').slice(0, 4).toUpperCase() || 'FILE';
  return (
    <button type="button" className="evidence-thumb" onClick={onOpen} title={item.file_name} aria-label={item.file_name}>
      {src ? <img src={src} alt="" /> : <span className="evidence-thumb-ext">{ext}</span>}
    </button>
  );
}

export function EvidenceList({
  items,
  onOpen,
  who,
}: {
  items: readonly DisputeEvidenceItem[];
  onOpen: (item: DisputeEvidenceItem) => Promise<void>;
  who: (item: DisputeEvidenceItem) => string;
}) {
  const [failure, setFailure] = useState<unknown>(null);
  if (items.length === 0) return null;
  return (
    <div className="attachment-picker">
      {failure ? <Callout tone="error">{explainFailure(failure).message}</Callout> : null}
      <ul className="attachment-list">
        {items.map((e) => (
          <li key={e.id}>
            <button
              type="button"
              className="link-button attachment-name"
              onClick={() => {
                setFailure(null);
                onOpen(e).catch((err: unknown) => setFailure(err));
              }}
            >
              {e.file_name}
            </button>
            <span className="faint">{formatFileSize(e.size_bytes)}</span>
            <span className="faint">{who(e)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EmailInput({
  value,
  onChange,
  placeholder,
  invalid,
  onKeyDown,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
}) {
  return (
    <TextInput
      value={value}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(invalid !== undefined ? { invalid } : {})}
      {...(onKeyDown !== undefined ? { onKeyDown } : {})}
      onChange={(v) => onChange(stripEmailWhitespace(v))}
    />
  );
}

function PasswordVisibilityIcon({ visible }: { visible: boolean }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  if (visible) {
    return (
      <svg {...common}>
        <path d="M10.733 5.076A10.744 10.744 0 0 1 12 5c7 0 10 5 10 5a13.3 13.3 0 0 1-1.67 2.68" />
        <path d="M6.61 6.61A13.3 13.3 0 0 0 2 12s3 3.66 7 5" />
        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
        <path d="M2 2l20 20" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** §3.7 — minimum length enforced in api-core on every password write. */
export { PASSWORD_MIN_LENGTH } from './password-policy.ts';

/** Password field with a show/hide control. Optional minLength shows a live X/12 counter. */
export function PasswordInput({
  value,
  onChange,
  placeholder,
  invalid,
  onKeyDown,
  autoComplete,
  minLength,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
  onKeyDown?: (e: ReactKeyboardEvent<HTMLInputElement>) => void;
  autoComplete?: string;
  /** When set, shows {typed}/{min} below the field — length only, no letter/number rules. */
  minLength?: number;
}) {
  const [visible, setVisible] = useState(false);
  const inputId = useId();
  const tooShort = minLength !== undefined && value.length > 0 && value.length < minLength;
  const met = minLength !== undefined && value.length >= minLength;

  const field = (
    <div className={`password-input${visible ? ' is-visible' : ''}${invalid || tooShort ? ' is-invalid' : ''}`}>
      <input
        id={inputId}
        className="control password-input-field"
        type={visible ? 'text' : 'password'}
        value={value}
        placeholder={placeholder ?? ''}
        aria-invalid={invalid || tooShort ? true : undefined}
        {...(autoComplete ? { autoComplete } : {})}
        {...(minLength !== undefined ? { minLength } : {})}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="password-input-toggle"
        aria-label={visible ? kitText('ui.password.hide') : kitText('ui.password.show')}
        aria-pressed={visible}
        aria-controls={inputId}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((v) => !v)}
      >
        <PasswordVisibilityIcon visible={visible} />
      </button>
    </div>
  );

  if (minLength === undefined) return field;

  return (
    <div className="password-input-wrap">
      {field}
      <span
        className={`password-length${met ? ' is-met' : ''}`}
        aria-live="polite"
        aria-label={`${value.length} of ${minLength} characters`}
      >
        {value.length}/{minLength}
      </span>
    </div>
  );
}

/** Half-width search field with an explicit Search button — API runs on click only. */
export function SearchRow({
  value,
  onChange,
  onSearch,
  placeholder = kitText('action.search'),
}: {
  value: string;
  onChange: (v: string) => void;
  onSearch: () => void;
  placeholder?: string;
}) {
  return (
    <div className="search-row">
      <TextInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onKeyDown={(e) => { if (e.key === 'Enter') onSearch(); }}
      />
      <button className="copy" type="button" onClick={onSearch}>{kitText('action.search')}</button>
    </div>
  );
}

/** Left subnav + right content — one section visible at a time. */
export function PageSplit({ children }: { children: ReactNode }) {
  return <div className="page-split">{children}</div>;
}

export function Subnav({ children, 'aria-label': ariaLabel }: { children: ReactNode; 'aria-label'?: string }) {
  return <aside className="subnav" aria-label={ariaLabel}>{children}</aside>;
}

export function SubnavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="subnav-group">
      <span className="subnav-label">{label}</span>
      {children}
    </div>
  );
}

export function SubnavItem({
  active,
  onClick,
  dot,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  /** Unseen activity in this section — the same red dot the rail uses. */
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <button type="button" aria-current={active ? 'page' : undefined} onClick={onClick}>
      <span className="subnav-label-text">{children}</span>
      {dot ? (
        <>
          <span className="nav-dot" aria-hidden="true" />
          <span className="sr-only">{kitText('shell.newActivity')}</span>
        </>
      ) : null}
    </button>
  );
}

export function PageSplitMain({ children }: { children: ReactNode }) {
  return <div className="page-split-main">{children}</div>;
}

/** One item in a left subnav — `onSelect` / `active` override the shared section state. */
export type SectionNavItem = {
  readonly id: string;
  readonly label: string;
  readonly active?: boolean;
  readonly onSelect?: () => void;
  /** Unseen activity in this section (a red dot beside the label). */
  readonly dot?: boolean;
};

export type SectionNavGroup = {
  readonly label: string;
  readonly items: readonly SectionNavItem[];
};

function SubnavFromGroups<T extends string>({
  groups,
  ariaLabel,
  section,
  onSection,
}: {
  groups: readonly SectionNavGroup[];
  ariaLabel: string;
  section: T;
  onSection: (id: T) => void;
}) {
  return (
    <Subnav aria-label={ariaLabel}>
      {groups.map((group) => (
        <SubnavGroup key={group.label} label={group.label}>
          {group.items.map((item) => (
            <SubnavItem
              key={item.id}
              active={item.active ?? section === item.id}
              onClick={item.onSelect ?? (() => onSection(item.id as T))}
              {...(item.dot ? { dot: true } : {})}
            >
              {item.label}
            </SubnavItem>
          ))}
        </SubnavGroup>
      ))}
    </Subnav>
  );
}

/** Left subnav + main pane — no page title. Use inside a screen that already has a head. */
export function SectionSplit<T extends string>({
  groups,
  ariaLabel,
  section,
  onSection,
  children,
}: {
  groups: readonly SectionNavGroup[];
  ariaLabel: string;
  section: T;
  onSection: (id: T) => void;
  children: ReactNode;
}) {
  return (
    <PageSplit>
      <SubnavFromGroups
        groups={groups}
        ariaLabel={ariaLabel}
        section={section}
        onSection={onSection}
      />
      <PageSplitMain>{children}</PageSplitMain>
    </PageSplit>
  );
}

/** Page title + left subnav + main pane — one section visible at a time. */
export function ScreenWithSections<T extends string>({
  title,
  headAside,
  groups,
  ariaLabel,
  section,
  onSection,
  children,
}: {
  title: string;
  headAside?: ReactNode;
  groups: readonly SectionNavGroup[];
  ariaLabel: string;
  section: T;
  onSection: (id: T) => void;
  children: ReactNode;
}) {
  return (
    <>
      <div className="page-head">
        <h1>{title}</h1>
        {headAside ?? null}
      </div>
      <SectionSplit
        groups={groups}
        ariaLabel={ariaLabel}
        section={section}
        onSection={onSection}
      >
        {children}
      </SectionSplit>
    </>
  );
}

/** Section state that resets when a scope key changes (e.g. brand login). */
export function useSectionNav<T extends string>(
  defaultSection: T,
  options?: { readonly resetTo?: T; readonly resetWhen?: unknown },
): [T, (section: T) => void] {
  const [section, setSection] = useState<T>(defaultSection);
  const resetTo = options?.resetTo;

  useEffect(() => {
    if (resetTo !== undefined) setSection(resetTo);
  }, [options?.resetWhen, resetTo]);

  return [section, setSection];
}

/* ------------------------------------------------------------------------ */
/* World map                                                                 */
/* ------------------------------------------------------------------------ */

type WorldOutlines = { readonly WORLD: Readonly<Record<string, string>>; readonly WORLD_WIDTH: number; readonly WORLD_HEIGHT: number };
let worldOutlines: WorldOutlines | null = null;
let worldOutlinesLoading: Promise<WorldOutlines> | null = null;

/**
 * The outlines are 160 KB the console never needs until somebody opens a
 * country report, so they arrive on first use rather than with every screen,
 * and a second map on the page reuses the first one's copy.
 */
function loadWorldOutlines(): Promise<WorldOutlines> {
  if (worldOutlines) return Promise.resolve(worldOutlines);
  worldOutlinesLoading ??= import('./world-map-data.ts').then((m) => {
    worldOutlines = m;
    return m;
  });
  return worldOutlinesLoading;
}

/**
 * A choropleth of where traffic came from: each country tinted by its share
 * of the busiest one, the rest left as land.
 *
 * `values` is keyed however the report keys its rows — `US`, `us`, `US-NJ`
 * all count for the United States — and `label(code, n)` names a country
 * for the hover title. Zoom is by the two buttons or a drag; the map does not
 * take the wheel, because a report is scrolled past more often than it is
 * zoomed into.
 */
export function WorldMap({ values, label, title }: {
  values: Iterable<readonly [string | null | undefined, number]>;
  label: (code: string, n: number) => string;
  title: string;
}) {
  const [world, setWorld] = useState<WorldOutlines | null>(worldOutlines);
  const [view, setView] = useState<MapViewport>({ x: 0, y: 0, scale: 1 });
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  useEffect(() => {
    let live = true;
    if (!world) void loadWorldOutlines().then((w) => { if (live) setWorld(w); });
    return () => { live = false; };
  }, [world]);
  const totals = useMemo(() => countryTotals(values), [values]);
  const max = useMemo(() => Math.max(0, ...totals.values()), [totals]);
  if (!world) return <div className="worldmap worldmap-loading" aria-busy="true" />;
  const { WORLD, WORLD_WIDTH, WORLD_HEIGHT } = world;
  const zoom = (factor: number) => setView((v) => zoomViewport(v, factor, WORLD_WIDTH, WORLD_HEIGHT));
  return (
    <div className="worldmap">
      <div className="worldmap-zoom" role="group" aria-label={kitText('map.zoom')}>
        <button type="button" className="ghost" onClick={() => zoom(1.6)} disabled={view.scale >= MAP_MAX_SCALE} aria-label={kitText('map.zoomIn')}>+</button>
        <button type="button" className="ghost" onClick={() => zoom(1 / 1.6)} disabled={view.scale <= MAP_MIN_SCALE} aria-label={kitText('map.zoomOut')}>−</button>
      </div>
      <svg
        ref={svgRef}
        className={`worldmap-svg${view.scale > 1 ? ' is-zoomed' : ''}`}
        viewBox={viewBoxOf(view, WORLD_WIDTH, WORLD_HEIGHT)}
        role="img"
        aria-label={title}
        onPointerDown={(e) => {
          if (view.scale <= 1) return;
          drag.current = { x: e.clientX, y: e.clientY, moved: false };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          const dx = e.clientX - d.x;
          const dy = e.clientY - d.y;
          d.x = e.clientX; d.y = e.clientY; d.moved = true;
          const drawn = svgRef.current?.getBoundingClientRect().width ?? WORLD_WIDTH;
          setView((v) => panViewport(v, dx, dy, WORLD_WIDTH, WORLD_HEIGHT, drawn));
        }}
        onPointerUp={() => { drag.current = null; }}
        onPointerCancel={() => { drag.current = null; }}
      >
        <title>{title}</title>
        {Object.entries(WORLD).map(([iso, d]) => {
          const n = totals.get(iso);
          const intensity = mapIntensity(n, max);
          return (
            <path
              key={iso}
              d={d}
              className={`worldmap-country${intensity > 0 ? ' has-traffic' : ''}`}
              style={intensity > 0 ? { fillOpacity: intensity } : undefined}
              data-iso={iso}
            >
              {n ? <title>{label(iso, n)}</title> : null}
            </path>
          );
        })}
      </svg>
    </div>
  );
}

/**
 * A column heading that explains itself on hover or keyboard focus (owner,
 * 2026-10-06, for "Won a player"). The full sentence is also its accessible
 * description, so a screen reader hears it without the hover.
 */
export function HeaderTip({ label, tip }: { label: string; tip: string }) {
  const id = useId();
  return (
    <button type="button" className="col-tip" aria-describedby={id}>
      {label}
      <span className="col-tip-mark" aria-hidden="true">ⓘ</span>
      <span className="col-tip-text" role="tooltip" id={id}>{tip}</span>
    </button>
  );
}
